/**
 * Goals hooks — TanStack Query for goals CRUD.
 * Follows use-habits.ts patterns: query keys, optimistic updates, invalidation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@turnifly/shared/stores'
import { useGoalsStore } from '@turnifly/shared/stores'
import type { Goal, GoalCreate, GoalEntry } from '@turnifly/shared/types'
import {
  fetchGoalsByYear,
  fetchGoalById,
  createGoal,
  updateGoal,
  deleteGoal,
  fetchGoalEntries,
  createGoalEntry,
  deleteGoalEntry,
  toggleGoalStep,
  shareGoalEntry,
  fetchProfileGoals,
  fetchGoalMonthlyProgress,
  fetchGoalWishes,
  createGoalWish,
  deleteGoalWish,
  announceGoal,
} from '@turnifly/shared/api'
import { triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'

// ── Query Keys ──

export const goalKeys = {
  all: ['goals'] as const,
  byYear: (userId: string, year: number) =>
    ['goals', 'year', userId, year] as const,
  single: (goalId: string) =>
    ['goals', 'single', goalId] as const,
  entries: (goalId: string) =>
    ['goals', 'entries', goalId] as const,
  profile: (userId: string) =>
    ['goals', 'profile', userId] as const,
  monthlyProgress: (goalId: string, year: number) =>
    ['goals', 'monthly-progress', goalId, year] as const,
  wishes: (goalId: string) =>
    ['goals', 'wishes', goalId] as const,
} as const

// ── Goals by Year ──

export function useGoalsByYear(year?: number) {
  const userId = useAuthStore((s) => s.user?.id) ?? ''
  const selectedYear = useGoalsStore((s) => s.selectedYear)
  const y = year ?? selectedYear

  return useQuery({
    queryKey: goalKeys.byYear(userId, y),
    queryFn: () => fetchGoalsByYear(userId, y),
    enabled: !!userId,
    staleTime: 30_000,
  })
}

// ── Single Goal ──

export function useGoal(goalId: string | null) {
  return useQuery({
    queryKey: goalKeys.single(goalId ?? ''),
    queryFn: () => fetchGoalById(goalId!),
    enabled: !!goalId,
    staleTime: 30_000,
  })
}

// ── Create Goal ──

export function useCreateGoal() {
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id) ?? ''

  return useMutation({
    mutationFn: (input: GoalCreate) => createGoal(userId, input),
    onMutate: async (input) => {
      const year = input.year
      const key = goalKeys.byYear(userId, year)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Goal[]>(key)

      const optimistic: Goal = {
        id: crypto.randomUUID(),
        user_id: userId,
        current_value: 0,
        achieved_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ...input,
      }

      queryClient.setQueryData<Goal[]>(key, (old) => [
        ...(old ?? []),
        optimistic,
      ])

      return { previous, key }
    },
    onError: (_err, _input, context) => {
      if (context?.previous && context?.key) {
        queryClient.setQueryData(context.key, context.previous)
      }
      hapticNotification('error')
    },
    onSettled: (_data, _error, input) => {
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: goalKeys.byYear(userId, input.year),
        })
      }, 300)
    },
  })
}

// ── Update Goal ──

export function useUpdateGoal() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ goalId, updates }: { goalId: string; updates: Record<string, unknown> }) =>
      updateGoal(goalId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalKeys.all })
    },
  })
}

// ── Delete Goal ──

export function useDeleteGoal() {
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id) ?? ''
  const selectedYear = useGoalsStore((s) => s.selectedYear)

  return useMutation({
    mutationFn: (goalId: string) => deleteGoal(goalId),
    onMutate: async (goalId) => {
      const key = goalKeys.byYear(userId, selectedYear)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Goal[]>(key)

      queryClient.setQueryData<Goal[]>(key, (old) =>
        (old ?? []).filter((g) => g.id !== goalId),
      )

      return { previous, key }
    },
    onError: (_err, _goalId, context) => {
      if (context?.previous && context?.key) {
        queryClient.setQueryData(context.key, context.previous)
      }
      hapticNotification('error')
    },
    onSettled: () => {
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: goalKeys.all })
      }, 300)
    },
  })
}

// ── Goal Entries ──

export function useGoalEntries(goalId: string | null) {
  return useQuery({
    queryKey: goalKeys.entries(goalId ?? ''),
    queryFn: () => fetchGoalEntries(goalId!),
    enabled: !!goalId,
    staleTime: 15_000,
  })
}

export function useCreateEntry() {
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id) ?? ''

  return useMutation({
    mutationFn: (input: {
      goal_id: string
      value: number
      step_index?: number | null
      note: string
      entry_date: string
    }) => createGoalEntry(userId, input),
    onMutate: async (input) => {
      const key = goalKeys.entries(input.goal_id)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<GoalEntry[]>(key)

      const optimistic: GoalEntry = {
        id: crypto.randomUUID(),
        user_id: userId,
        goal_id: input.goal_id,
        value: input.value,
        step_index: input.step_index ?? null,
        note: input.note,
        is_shared: false,
        entry_date: input.entry_date,
        created_at: new Date().toISOString(),
      }

      queryClient.setQueryData<GoalEntry[]>(key, (old) => [
        optimistic,
        ...(old ?? []),
      ])

      return { previous, key }
    },
    onError: (_err, _input, context) => {
      if (context?.previous && context?.key) {
        queryClient.setQueryData(context.key, context.previous)
      }
      hapticNotification('error')
    },
    onSettled: (_data, _error, input) => {
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: goalKeys.entries(input.goal_id) })
        void queryClient.invalidateQueries({ queryKey: goalKeys.all })
      }, 300)
    },
  })
}

export function useDeleteEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ entryId }: { entryId: string; goalId: string }) =>
      deleteGoalEntry(entryId),
    onSuccess: (_data, { goalId }) => {
      void queryClient.invalidateQueries({ queryKey: goalKeys.entries(goalId) })
      void queryClient.invalidateQueries({ queryKey: goalKeys.all })
    },
  })
}

export function useToggleStep() {
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id) ?? ''

  return useMutation({
    mutationFn: (args: {
      goalId: string
      stepIndex: number
      label: string
      isCompleting: boolean
    }) => toggleGoalStep(userId, args.goalId, args.stepIndex, args.label, args.isCompleting),
    onSuccess: (_data, { goalId }) => {
      void queryClient.invalidateQueries({ queryKey: goalKeys.entries(goalId) })
      void queryClient.invalidateQueries({ queryKey: goalKeys.all })
    },
  })
}

export function useShareEntry() {
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id) ?? ''

  return useMutation({
    mutationFn: ({ entry, goal }: { entry: GoalEntry; goal: Goal }) =>
      shareGoalEntry(userId, entry, goal),
    onSuccess: (_data, { entry }) => {
      void queryClient.invalidateQueries({ queryKey: goalKeys.entries(entry.goal_id) })
    },
  })
}

// ── Monthly Progress (achieved values per month for roadmap) ──

export function useGoalMonthlyProgress(goalId: string | null, year: number) {
  return useQuery({
    queryKey: goalKeys.monthlyProgress(goalId ?? '', year),
    queryFn: () => fetchGoalMonthlyProgress(goalId!, year),
    enabled: !!goalId,
    staleTime: 30_000,
  })
}

// ── Announce Goal ──

export function useAnnounceGoal() {
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id) ?? ''

  return useMutation({
    mutationFn: (goal: Goal) => announceGoal(userId, goal),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalKeys.all })
    },
  })
}

// ── Goal Wishes ──

export function useGoalWishes(goalId: string | null) {
  return useQuery({
    queryKey: goalKeys.wishes(goalId ?? ''),
    queryFn: () => fetchGoalWishes(goalId!),
    enabled: !!goalId,
    staleTime: 30_000,
  })
}

export function useCreateWish() {
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id) ?? ''

  return useMutation({
    mutationFn: ({ goalId, text }: { goalId: string; text: string }) =>
      createGoalWish(goalId, userId, text),
    onSuccess: (_data, { goalId }) => {
      queryClient.invalidateQueries({ queryKey: goalKeys.wishes(goalId) })
    },
  })
}

export function useDeleteWish() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ wishId }: { wishId: string; goalId: string }) =>
      deleteGoalWish(wishId),
    onSuccess: (_data, { goalId }) => {
      queryClient.invalidateQueries({ queryKey: goalKeys.wishes(goalId) })
    },
  })
}

// ── Profile Goals (all years, for profile stats tab) ──

export function useProfileGoals(userId: string | undefined) {
  return useQuery({
    queryKey: goalKeys.profile(userId ?? ''),
    queryFn: () => fetchProfileGoals(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  })
}
